import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Check, Loader2, Car } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';
import { formatBusinessDate, formatBusinessTime, formatMoney } from '@/lib/format';

// ─── Types ───────────────────────────────────────────────

interface Transaction {
    id: number;
    amount: number;
    /** USD for a CIF payment, GHS for customs: the amount is printed in its own currency. */
    currency: string | null;
    transaction_at: string;
    description: string | null;
}

interface AssignVehicleTransactionSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    transaction: Transaction | null;
    /** Existing vehicle_transaction ID when reassigning */
    existingVtId?: number | null;
    /** Existing transaction type when reassigning */
    existingTransactionType?: string | null;
}

interface FleetVehicle {
    id: number;
    make: string;
    model: string;
    vin: string;
    color: string;
    manufactured_at: number;
    vehicle_procurement: { listing_url: string | null } | null;
    vehicle_registrations: { number: string } | null;
    vehicle_transactions: { transaction_type: string }[];
}

type VehicleTransactionType = 'cif_30' | 'cif_70' | 'tax_bill' | 'port_charges' | 'landed_cost' | 'steering_changed' | 'levy';

// 'levy' is deliberately excluded from `availableTypes` below — it's assigned container-level only,
// via CustomsPaymentsPanel's split, never through this single-vehicle assign/reassign sheet. The
// label is still needed here to render the badge on a vehicle that already holds a levy transaction.
const TRANSACTION_TYPE_LABELS: Record<VehicleTransactionType, string> = {
    cif_30: 'CIF 30%',
    cif_70: 'CIF 70%',
    tax_bill: 'Tax Bill',
    port_charges: 'Port Charges',
    landed_cost: 'Landed Cost',
    steering_changed: 'Steering Conversion',
    levy: 'CHA Membership Levy',
};

// ─── Helpers ─────────────────────────────────────────────

function getColorValue(color: string): string {
    const trimmed = color.trim();
    if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
    if (trimmed.startsWith('#')) return trimmed;
    return trimmed;
}

function getStockId(url: string | null): string | null {
    if (!url) return null;
    const segments = url.replace(/\/+$/, '').split('/');
    return segments[segments.length - 1] || null;
}

function getSbtPhotoUrl(listingUrl: string | null): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg`;
}

// ─── Component ───────────────────────────────────────────

export function AssignVehicleTransactionSheet({
    open,
    onOpenChange,
    transaction,
    existingVtId,
    existingTransactionType,
}: AssignVehicleTransactionSheetProps) {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);
    const [transactionType, setTransactionType] = useState<VehicleTransactionType | ''>('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    // Reset state when sheet opens/closes
    useEffect(() => {
        if (open) {
            setSelectedVehicleId(null);
            setTransactionType(
                existingTransactionType as VehicleTransactionType || ''
            );
            setSearchTerm('');
        }
    }, [open, existingTransactionType]);

    // Fetch vehicles
    const { data: vehicles, isLoading } = useQuery({
        queryKey: ['fleet-vehicles-for-assign', debouncedSearch],
        queryFn: async () => {
            // Pre-query: find vehicles matching by registration number or listing URL (stock ID)
            let extraIds: number[] = [];
            if (debouncedSearch) {
                const [regResult, procResult] = await Promise.all([
                    supabase
                        .from('vehicles')
                        .select('id, vehicle_registrations!inner(number)')
                        .ilike('vehicle_registrations.number', `%${debouncedSearch}%`),
                    supabase
                        .from('vehicle_procurement')
                        .select('vehicle_id')
                        .ilike('listing_url', `%${debouncedSearch}%`),
                ]);
                const regIds = regResult.data?.map((v: any) => v.id) || [];
                const procIds = procResult.data?.map((v: any) => v.vehicle_id) || [];
                extraIds = [...new Set([...regIds, ...procIds])];
            }

            let query = (supabase
                .from('vehicles')
                .select('id, make, model, vin, color, manufactured_at, vehicle_procurement(listing_url), vehicle_registrations(number), vehicle_transactions(transaction_type)') as any)
                .order('created_at', { ascending: false })
                .limit(50);
            if (debouncedSearch) {
                if (extraIds.length > 0) {
                    query = query.or(`make.ilike.%${debouncedSearch}%,model.ilike.%${debouncedSearch}%,vin.ilike.%${debouncedSearch}%,id.in.(${extraIds.join(',')})`);
                } else {
                    query = query.or(`make.ilike.%${debouncedSearch}%,model.ilike.%${debouncedSearch}%,vin.ilike.%${debouncedSearch}%`);
                }
            }
            const { data, error } = await query;
            if (error) throw error;
            return data as FleetVehicle[];
        },
        enabled: open,
        staleTime: 30_000,
    });

    // Selected vehicle data
    const selectedVehicle = vehicles?.find(v => v.id === selectedVehicleId) ?? null;
    const existingTypes = selectedVehicle?.vehicle_transactions?.map(vt => vt.transaction_type) ?? [];
    const availableTypes: VehicleTransactionType[] = ['cif_30', 'cif_70', 'tax_bill', 'port_charges', 'landed_cost'];

    // Reset type if not available when vehicle changes
    const effectiveType = transactionType && availableTypes.includes(transactionType)
        ? transactionType : '';

    // Exclude vehicles that already have the selected transaction type
    const filteredVehicles = useMemo(() => {
        if (!vehicles) return [];
        if (!effectiveType) return vehicles;
        return vehicles.filter(v => {
            const vTypes = v.vehicle_transactions?.map(vt => vt.transaction_type) ?? [];
            return !vTypes.includes(effectiveType);
        });
    }, [vehicles, effectiveType]);

    // Clear selection if vehicle no longer in filtered list
    useEffect(() => {
        if (selectedVehicleId && !filteredVehicles.some(v => v.id === selectedVehicleId)) {
            setSelectedVehicleId(null);
        }
    }, [filteredVehicles, selectedVehicleId]);

    // Assignment mutation
    const assignMutation = useMutation({
        mutationFn: async () => {
            if (!transaction || !selectedVehicleId || !effectiveType) {
                throw new Error('Please select a vehicle and transaction type');
            }

            // If reassigning, delete the old link first
            if (existingVtId) {
                const { error: delErr } = await supabase
                    .from('vehicle_transactions')
                    .delete()
                    .eq('id', existingVtId);
                if (delErr) throw delErr;
            }

            const { error } = await supabase
                .from('vehicle_transactions')
                .insert({
                    vehicle_id: selectedVehicleId,
                    transaction_id: transaction.id,
                    transaction_type: effectiveType,
                });
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success(existingVtId ? 'Transaction reassigned' : 'Transaction assigned to vehicle');
            queryClient.invalidateQueries({ queryKey: ['all-vehicle-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['vehicle-transaction-stats'] });
            queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-for-assign'] });
            queryClient.invalidateQueries({ queryKey: ['vehicles'] });
            onOpenChange(false);
        },
        onError: (e: any) => {
            toast.error(e?.message || 'Failed to assign transaction');
        },
    });

    const isReassign = !!existingVtId;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                hideOverlay
                className="w-[500px] m-4 h-[calc(100vh-32px)] rounded-xl border flex flex-col"
            >
                <SheetHeader>
                    <SheetTitle>{isReassign ? 'Reassign Transaction' : 'Assign to Vehicle'}</SheetTitle>
                </SheetHeader>

                <div className="mt-4 flex flex-col flex-1 min-h-0">
                    {/* Transaction Summary */}
                    {transaction && (
                        <div className="p-4 bg-muted/50 rounded-lg mb-4 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Amount</span>
                                <span className="text-lg font-bold">{formatMoney(transaction.amount, transaction.currency)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Date</span>
                                <span className="text-sm">
                                    {`${formatBusinessDate(transaction.transaction_at)} · ${formatBusinessTime(transaction.transaction_at)}`}
                                </span>
                            </div>
                            {transaction.description && (
                                <div className="pt-2 border-t">
                                    <span className="text-xs text-muted-foreground block mb-1">Description</span>
                                    <p className="text-sm">{transaction.description}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Transaction Type */}
                    <div className="mb-4 space-y-2">
                        <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                            Transaction Type <span className="text-destructive">*</span>
                        </Label>
                        <Select
                            value={effectiveType}
                            onValueChange={v => setTransactionType(v as VehicleTransactionType)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select type..." />
                            </SelectTrigger>
                            <SelectContent>
                                {availableTypes.map(t => (
                                    <SelectItem key={t} value={t}>
                                        {TRANSACTION_TYPE_LABELS[t]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Search */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by make, model, VIN, stock ID, reg..."
                            className="pl-9 focus-visible:ring-ring"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Vehicle List */}
                    <ScrollArea className="flex-1">
                        <div className="space-y-2 pr-4">
                            {isLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : vehicles?.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <p className="text-sm">No vehicles found</p>
                                    {searchTerm && <p className="text-xs mt-1">Try a different search term</p>}
                                </div>
                            ) : filteredVehicles.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <p className="text-sm">{effectiveType ? 'All vehicles already have this type assigned' : 'No vehicles found'}</p>
                                    {searchTerm && <p className="text-xs mt-1">Try a different search term</p>}
                                </div>
                            ) : (
                                filteredVehicles.map(v => {
                                    const isSelected = selectedVehicleId === v.id;
                                    const vTypes = v.vehicle_transactions?.map(vt => vt.transaction_type) ?? [];
                                    const stockId = getStockId(v.vehicle_procurement?.listing_url ?? null);

                                    return (
                                        <button
                                            key={v.id}
                                            onClick={() => setSelectedVehicleId(v.id)}
                                            className={`w-full text-left p-3 rounded-lg border transition-all hover:bg-muted/50 ${
                                                isSelected
                                                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                                    : 'border-border'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                {(() => {
                                                    const photoUrl = getSbtPhotoUrl(v.vehicle_procurement?.listing_url ?? null);
                                                    if (photoUrl) {
                                                        return (
                                                            <>
                                                                <img
                                                                    src={photoUrl}
                                                                    alt=""
                                                                    className="h-9 w-9 rounded-lg border border-gray-200 object-cover shrink-0"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                                                    }}
                                                                />
                                                                <div
                                                                    className="h-9 w-9 rounded-lg border border-gray-200 shrink-0 hidden"
                                                                    style={{ backgroundColor: getColorValue(v.color) || '#e5e7eb' }}
                                                                />
                                                            </>
                                                        );
                                                    }
                                                    return (
                                                        <div
                                                            className="h-9 w-9 rounded-lg border border-gray-200 shrink-0"
                                                            style={{ backgroundColor: getColorValue(v.color) || '#e5e7eb' }}
                                                        />
                                                    );
                                                })()}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-sm truncate">
                                                            {v.make} {v.model}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">{v.manufactured_at}</span>
                                                        {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        {v.vehicle_registrations?.number && (
                                                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                                                {v.vehicle_registrations.number}
                                                            </code>
                                                        )}
                                                        <code className="text-[10px] text-muted-foreground/60 font-mono">{v.vin}</code>
                                                    </div>
                                                </div>
                                                <div className="shrink-0 flex flex-col items-end gap-1">
                                                    {vTypes.length > 0 && (
                                                        <div className="flex flex-wrap justify-end gap-1">
                                                            {vTypes.map((t, i) => (
                                                                <Badge key={`${t}-${i}`} variant="outline" className="text-[10px] px-1.5 py-0">
                                                                    {TRANSACTION_TYPE_LABELS[t as VehicleTransactionType] || t}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {stockId && (
                                                        <code className="text-[10px] text-muted-foreground/70 font-mono">{stockId}</code>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </ScrollArea>

                    {/* Actions */}
                    <div className="pt-4 border-t mt-4">
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex-1"
                                onClick={() => assignMutation.mutate()}
                                disabled={!selectedVehicleId || !effectiveType || assignMutation.isPending}
                            >
                                {assignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                {isReassign ? 'Reassign' : 'Assign'}
                            </Button>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
